/**
 * What a map asset looks like on the board (`projects/map-assets.md` Phase 3, `decisions/0193`):
 * the pure step from the encounter's combatants to one {@link AssetLook} per placed asset. The
 * painter (`assetArt.ts`) draws a look; the board layer (`assetLayer.ts`) places it. Nothing here
 * touches a canvas, so the rules for bands and trunk sections are tested directly.
 *
 * A combatant is drawn as an asset when `drawnAssetId` names a catalogue entry: its `assetId`, or
 * a plain object recognised by name (the landing zone's Hauler Wrecks). An unknown id falls back to
 * the plain object token (orchestrator call 5), so it gets no look here.
 */
import { type Combatant, type MapAssetId, drawnAssetId, hasStatus, mapAsset } from "@lancer-vtt/domain";
import { asSquareCell, footprintBox } from "../combatCells";
import { truncateLabel } from "../labelLayout";

/** Intact, damaged at half HP or below, or destroyed and still placed. */
export type AssetBand = "intact" | "damaged" | "destroyed";

/** Where one fallen-trunk section sits in its log (orchestrator call 8). */
export type TrunkPlace = {
  /** `root` is section 0, `crown` the last, `single` a one-section log. */
  role: "root" | "middle" | "crown" | "single";
  /** The direction the log runs from its root end, as painted: `e` along +x, `s` along +y, and the reverses. */
  dir: "e" | "w" | "s" | "n";
  /** A neighbour section on that side is gone, so this end is splintered. */
  brokenBefore: boolean;
  brokenAfter: boolean;
};

/** Everything the painter reads. Two assets with the same look share one cached sprite. */
export type AssetLook = {
  assetId: MapAssetId;
  /** Footprint side in cells. */
  cells: number;
  band: AssetBand;
  /** Varies the drawing between two assets of the same kind; from the combatant id. */
  seed: number;
  trunk?: TrunkPlace;
};

/** Damaged at or below half HP (3.1). */
export function damageBand(hp: number, maxHp: number): AssetBand {
  return hp * 2 <= maxHp ? "damaged" : "intact";
}

/** The sprite cache key: every field of the look. */
export function assetLookKey(look: AssetLook): string {
  const t = look.trunk;
  return [look.assetId, look.cells, look.band, look.seed, t ? `${t.role}:${t.dir}:${+t.brokenBefore}${+t.brokenAfter}` : "-"].join("|");
}

/** The number of distinct drawings per asset kind; a seed is folded into this range so the cache stays small. */
export const ASSET_VARIANTS = 4;

function seedOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return (h >>> 0) % ASSET_VARIANTS;
}

/** One placed asset: its look and its footprint's top-left cell. */
export type PlacedAsset = { id: string; look: AssetLook; minX: number; minY: number };

const alive = (c: Combatant | undefined): boolean => c !== undefined && c.pos != null && !hasStatus(c.statuses, "Destroyed");

/** A section's index in its group, from the `${groupId}:${i}` id `spawnObjectGroup` mints. */
function sectionIndex(c: Combatant): number | null {
  if (!c.objectGroupId || !c.id.startsWith(`${c.objectGroupId}:`)) return null;
  const i = Number(c.id.slice(c.objectGroupId.length + 1));
  return Number.isInteger(i) && i >= 0 ? i : null;
}

/**
 * The trunk place of section `c`: its role from its index, which ends are splintered from its
 * neighbours' state, and the log's direction from where another live section sits. A log with no
 * other live section runs east.
 */
function trunkPlace(c: Combatant, group: Combatant[]): TrunkPlace {
  const byIndex = new Map<number, Combatant>();
  let count = 0;
  for (const m of group) {
    const i = sectionIndex(m);
    if (i === null) continue;
    byIndex.set(i, m);
    count = Math.max(count, i + 1);
  }
  const i = sectionIndex(c) ?? 0;
  const role = count <= 1 ? "single" : i === 0 ? "root" : i === count - 1 ? "crown" : "middle";
  let dir: TrunkPlace["dir"] = "e";
  const at = asSquareCell(c.pos!);
  for (const [j, m] of byIndex) {
    if (j === i || !alive(m)) continue;
    const p = asSquareCell(m.pos!);
    const dx = Math.sign((p.x - at.x) * (j - i));
    const dy = Math.sign((p.y - at.y) * (j - i));
    if (dx !== 0 && dy === 0) dir = dx > 0 ? "e" : "w";
    else if (dy !== 0 && dx === 0) dir = dy > 0 ? "s" : "n";
    break;
  }
  return { role, dir, brokenBefore: i > 0 && !alive(byIndex.get(i - 1)), brokenAfter: i < count - 1 && !alive(byIndex.get(i + 1)) };
}

/**
 * Every placed combatant drawn as an asset, as a look and a footprint corner, sorted north to south
 * then west to east so a sprite's south-east shadow never lands on top of a later neighbour. A
 * destroyed asset still placed (a name-recognised wreck, which the remains sweep never unplaces, or a
 * GM re-placing one) draws in the destroyed band.
 */
export function placedAssets(combatants: readonly Combatant[]): PlacedAsset[] {
  const groups = new Map<string, Combatant[]>();
  for (const c of combatants) {
    if (c.objectGroupId) groups.set(c.objectGroupId, [...(groups.get(c.objectGroupId) ?? []), c]);
  }
  const out: PlacedAsset[] = [];
  for (const c of combatants) {
    const assetId = drawnAssetId(c);
    if (c.pos == null || assetId === undefined) continue;
    const entry = mapAsset(assetId)!;
    const { minX, minY, n } = footprintBox(asSquareCell(c.pos), c.base.size);
    const destroyed = hasStatus(c.statuses, "Destroyed");
    const look: AssetLook = { assetId, cells: n, band: destroyed ? "destroyed" : damageBand(c.current.hp, c.base.maxHp), seed: seedOf(c.id) };
    if (entry.sections && c.objectGroupId) look.trunk = trunkPlace(c, groups.get(c.objectGroupId)!);
    out.push({ id: c.id, look, minX, minY });
  }
  return out.sort((a, b) => a.minY - b.minY || a.minX - b.minX);
}

/** Whether the board draws `c` as an asset rather than as a token. */
export function isDrawnAsAsset(c: Combatant): boolean {
  return drawnAssetId(c) !== undefined;
}

/**
 * A token's label pill text. A map asset's carries its HP, because its label is the only place the
 * board shows it (orchestrator call 10: objects are public), and is never truncated: it shows only on
 * hover or selection and stays out of the label solve, so it contends for no room.
 */
export function tokenLabelText(c: Combatant): string {
  return isDrawnAsAsset(c) ? `${c.name.replace(SECTION_SUFFIX, "")} · ${c.current.hp}/${c.base.maxHp} HP` : truncateLabel(c.name);
}

/** A sectioned object's `(i/N)` name suffix (`spawnObjectGroup`). A log's section reads as "Log" on hover, not "Log (2/4)". */
const SECTION_SUFFIX = / \(\d+\/\d+\)$/;
